import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { Link } from '../../../util/router';

const headlines = [
  "Unlock $100K to $1M Extra Life Insurance Sales—Risk Free",
  "Multiply Life Sales by $100K–$1M with One Simple Training",
  "End Price Wars—Add $100K to $1M in Life Sales Effortlessly",
  "Close Bigger Life Insurance Deals Faster and Risk Free",
  "Your Shortcut to $100K+ to $1 Million in Life Insurance Sales Growth"
];

const problemPoints = [
  {
    title: "Stuck in a price war?",
    description: "Racing to the bottom with every quote isn't sustainable—and it's exhausting."
  },
  {
    title: "Spending a fortune on leads that go nowhere?",
    description: "You're buying leads, but your team can't consistently close them."
  },
  {
    title: "Producers not confident selling life insurance?",
    description: "They stick to what's comfortable, leaving life insurance revenue on the table."
  },
  {
    title: "No consistent sales process?",
    description: "Every call is different. Some work, most don't. There's no repeatable system."
  }
];

const solutionFeatures = [
  {
    title: "Stop Selling on Price",
    description: "Shift from competing on cost to educating clients on value. When clients understand why they need life insurance, price becomes secondary."
  },
  {
    title: "Multiply Revenue",
    description: "A 4.5-minute educational video plus an 8-step guided script turns every client conversation into a life insurance opportunity."
  },
  {
    title: "Train Anyone",
    description: "Available in English, Spanish, Cantonese, and Mandarin. Your entire team can use it—no matter their background or experience level."
  },
  {
    title: "Create Lifelong Clients",
    description: "When you help clients protect their families, you build trust that lasts for years—and generates referrals."
  }
];

const howItWorks = [
  {
    step: "1",
    title: "Watch the 4.5-Minute Video",
    description: "Send your client a short, educational video that explains life insurance using the simple 'Stairs and Buckets' concept. They understand immediately."
  },
  {
    step: "2",
    title: "Follow the 8-Step Script",
    description: "Use our proven conversation guide to walk through the client's needs. No pressure, no memorization—just a natural conversation that leads to a sale."
  },
  {
    step: "3",
    title: "Use Team Analytics & Training",
    description: "Track who's watching, who's engaging, and who's ready to buy. Join our free training webinars to sharpen your skills."
  }
];

const testimonials = [
  {
    name: "Jen",
    quote: "This video is amazing... I wrote my biggest policy ever on my first try using it."
  },
  {
    name: "Art",
    quote: "My team sold over $200K in premium in six months using this."
  },
  {
    name: "Will",
    quote: "I consistently sell larger whole and universal life policies now."
  }
];

const faqs = [
  {
    question: "My team resists scripts. Will this work for them?",
    answer: "This isn't a robotic script—it's a conversation guide. It gives your team a framework to follow while staying natural and authentic. Most agents find it takes the pressure off, not adds to it."
  },
  {
    question: "We don't have time for training.",
    answer: "The video is 4.5 minutes. The script fits on one page. And our optional training webinars are just one hour. This is designed for busy agencies that need results fast."
  },
  {
    question: "Our current process works fine. Why change?",
    answer: "If you're leaving life insurance revenue on the table, your process has room to improve. Stairs and Buckets helps you find that revenue without complicated systems or major overhauls."
  }
];

function StairsBucketsLandingPage() {
  const [currentHeadline, setCurrentHeadline] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeadline((prev) => (prev + 1) % headlines.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-green-700 to-green-600 text-white py-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 min-h-[3em] md:min-h-[2em] flex items-center justify-center">
            <span key={currentHeadline} className="animate-fade-in">
              {headlines[currentHeadline]}
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-green-100 mb-10 max-w-3xl mx-auto">
            The proven system that transforms every client conversation into a life insurance opportunity.
          </p>
          <Link
            to="/checkout?product=sb"
            className="inline-block bg-white text-green-700 font-bold py-4 px-12 rounded-lg text-xl hover:bg-green-50 transition-colors shadow-lg"
          >
            Get Started Now
          </Link>
          <p className="text-green-200 mt-6 text-sm">
            100% Money-Back Guarantee within 3 days
          </p>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            Sound Familiar?
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            You're not alone. These are the challenges keeping agencies stuck.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {problemPoints.map((point, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md border-l-4 border-green-600">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{point.title}</h3>
                <p className="text-gray-600">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            The Solution: Stairs and Buckets
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            A simple, proven system that helps your team sell more life insurance—without the awkward conversations.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {solutionFeatures.map((feature, index) => (
              <div key={index} className="bg-green-50 p-6 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="bg-green-600 rounded-full p-2 flex-shrink-0">
                    <CheckIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => (
              <div key={index} className="text-center">
                <div className="bg-green-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            What Agents Are Saying
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">"{testimonial.quote}"</p>
                <p className="text-gray-900 font-semibold">— {testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-green-700">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
            Ready to Transform Your Life Insurance Sales?
          </h2>
          <p className="text-xl text-green-100 text-center mb-12">
            Get started today with our risk-free guarantee.
          </p>

          <div className="bg-white rounded-2xl p-8 shadow-xl max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">What's Included</h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-gray-700">10 team seats included</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-gray-700">Unlimited video sends</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-gray-700">Team analytics dashboard</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-gray-700">Free training webinars</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-gray-700">4 language options</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-gray-700">8-step guided script</span>
              </div>
            </div>

            <div className="text-center">
              <p className="text-gray-600 mb-4">Choose monthly or annual billing on the next page</p>
              <Link
                to="/checkout?product=sb"
                className="inline-block w-full sm:w-auto bg-green-600 text-white font-bold py-4 px-12 rounded-lg text-xl hover:bg-green-700 transition-colors"
              >
                Get Started Now
              </Link>
            </div>
          </div>

          <p className="text-center text-green-100 mt-8">
            100% Money-Back Guarantee within 3 days. No questions asked.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            Common Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-medium text-gray-900">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Stop Letting Good Leads Slip Through Your Fingers
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Every client conversation is a life insurance opportunity.
            Start closing those deals today with Stairs and Buckets.
          </p>
          <Link
            to="/checkout?product=sb"
            className="inline-block bg-green-600 text-white font-bold py-4 px-12 rounded-lg text-xl hover:bg-green-700 transition-colors shadow-lg"
          >
            Get Started Now
          </Link>
          <p className="text-gray-400 mt-6 text-sm">
            100% Money-Back Guarantee within 3 days
          </p>
        </div>
      </section>
    </div>
  );
}

export default StairsBucketsLandingPage;
